import React, { useContext, useEffect, useState } from "react";
import FrontendLayout from "../../layouts/FrontendLayout";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import Spinner from "../../components/common/Spinner";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  const { login, user } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user?.role?.roles_id === 1) {
        navigate("/masteradmin");
      } else {
        navigate("/admin");
      }
    }
  }, [user]);

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    setIsLoading(true);
    setError("");
    const credentials = { email, password, remember };
    try {
      await login(credentials);
    } catch (error) {
      setError("Invalid email or password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Login to Your Allied Health Account | Allied Health.com.au
        </title>

        <meta
          name="description"
          content="Access your Allied Health.com.au account to manage your profile, connect with clients, and update your services. Secure login for health professionals and users across Australia."
        />

        <meta
          name="keywords"
          content="allied health login, healthcare provider login, Australia health practitioner portal, allied health account access, NDIS provider login, client login Allied Health"
        />

        <meta name="robots" content="noindex, nofollow" />

        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        <meta charset="UTF-8" />
      </Helmet>

      <FrontendLayout>
        <section>
          <div className="container mx-auto px-3 pt-[40px] pb-[40px] md:pt-[50px] md:pb-[50px] flex justify-center items-center flex-col">
            <div className="max-w-[630px] w-full rounded-[18px] bg-white sm:px-14 px-5 py-8 shadow-card">
              <h1 className="text-dark text-[28px] sm:text-[34px] leading-relaxed font-semibold">
                Welcome back!
              </h1>
              <span className="text-dark text-xl mb-4 inline-block">
                Enter email and password to login your account
              </span>

              {/* {error && <p className="text-red-500 mb-4">{error}</p>} */}

              <form onSubmit={handleSubmit}>
                <div className="mb-4 sm:mb-5">
                  <label
                    htmlFor="email"
                    className="mb-2 inline-block text-dark font-semibold text-xl"
                  >
                    Email
                  </label>
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    id="email"
                    className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-lg sm:text-xl"
                    placeholder="Enter your email address"
                    required
                  />
                </div>
                <div className="mb-4 sm:mb-5">
                  <label
                    htmlFor="Password"
                    className="mb-2 inline-block text-dark font-semibold text-xl"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      type={showPassword ? "text" : "password"}
                      id="Password"
                      className="password-input border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-lg sm:text-xl"
                      placeholder="Enter password"
                      required
                    />
                    <div
                      onClick={togglePasswordVisibility}
                      className="password-toggle absolute top-1/2 right-5 -translate-y-2/4 cursor-pointer"
                    >
                      {showPassword ? (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.0003 3C17.3924 3 21.8784 6.87976 22.8189 12C21.8784 17.1202 17.3924 21 12.0003 21C6.60812 21 2.12215 17.1202 1.18164 12C2.12215 6.87976 6.60812 3 12.0003 3ZM12.0003 19C16.2359 19 19.8603 16.052 20.7777 12C19.8603 7.94803 16.2359 5 12.0003 5C7.7646 5 4.14022 7.94803 3.22278 12C4.14022 16.052 7.7646 19 12.0003 19ZM12.0003 16.5C9.51498 16.5 7.50026 14.4853 7.50026 12C7.50026 9.51472 9.51498 7.5 12.0003 7.5C14.4855 7.5 16.5003 9.51472 16.5003 12C16.5003 14.4853 14.4855 16.5 12.0003 16.5ZM12.0003 14.5C13.381 14.5 14.5003 13.3807 14.5003 12C14.5003 10.6193 13.381 9.5 12.0003 9.5C10.6196 9.5 9.50026 10.6193 9.50026 12C9.50026 13.3807 10.6196 14.5 12.0003 14.5Z"
                            fill="black"
                          ></path>
                        </svg>
                      ) : (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.8827 19.2968C16.1814 20.3755 14.1638 21.0002 12.0003 21.0002C6.60812 21.0002 2.12215 17.1204 1.18164 12.0002C1.61832 9.62283 2.81932 7.5129 4.52047 5.93457L1.39366 2.80777L2.80788 1.39355L22.6069 21.1925L21.1927 22.6068L17.8827 19.2968ZM5.9356 7.3497C4.60673 8.56015 3.6378 10.1672 3.22278 12.0002C4.14022 16.0521 7.7646 19.0002 12.0003 19.0002C13.5997 19.0002 15.112 18.5798 16.4243 17.8384L14.396 15.8101C13.7023 16.2472 12.8808 16.5002 12.0003 16.5002C9.51498 16.5002 7.50026 14.4854 7.50026 12.0002C7.50026 11.1196 7.75317 10.2981 8.19031 9.60442L5.9356 7.3497ZM12.9139 14.328L9.67246 11.0866C9.5613 11.3696 9.50026 11.6777 9.50026 12.0002C9.50026 13.3809 10.6196 14.5002 12.0003 14.5002C12.3227 14.5002 12.6309 14.4391 12.9139 14.328ZM20.8068 16.5925L19.376 15.1617C20.0319 14.2268 20.5154 13.1586 20.7777 12.0002C19.8603 7.94818 16.2359 5.00016 12.0003 5.00016C11.1544 5.00016 10.3329 5.11773 9.55249 5.33818L7.97446 3.76015C9.22127 3.26959 10.5793 3.00016 12.0003 3.00016C17.3924 3.00016 21.8784 6.87993 22.8189 12.0002C22.5067 13.6998 21.8038 15.2628 20.8068 16.5925ZM11.7229 7.50857C11.8146 7.50299 11.9071 7.50016 12.0003 7.50016C14.4855 7.50016 16.5003 9.51488 16.5003 12.0002C16.5003 12.0933 16.4974 12.1858 16.4919 12.2775L11.7229 7.50857Z"
                            fill="#393939"
                          ></path>
                          ';
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 justify-between items-center">
                  <div className="flex items-center">
                    <input
                      id="default-checkbox"
                      type="checkbox"
                      className="w-4 sm:w-6 h-5 sm:h-6 text-blue-600 bg-gray-100 border-gray-300 rounded"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                    />
                    <label
                      htmlFor="default-checkbox"
                      className="ms-2 text-base sm:text-xl font-medium text-dark"
                    >
                      Remember me
                    </label>
                  </div>
                  <Link
                    to="/forgotpassword"
                    className="text-right font-semibold text-blue text-xl ml-auto"
                  >
                    Forgot Password?
                  </Link>
                </div>
                <button
                  type="submit"
                  className="h-auto py-3 px-3 sm:h-14 bg-blue w-full text-center text-white font-semibold text-lg sm:text-2xl rounded-xl mt-6 mb-4"
                >
                  Login
                </button>
              </form>
              <p className="text-center text-dark text-xl font-medium">
                Don’t have an account?{" "}
                <Link to="/register" className="text-blue font-semibold">
                  Register
                </Link>
              </p>
            </div>
          </div>
        </section>
      </FrontendLayout>
    </>
  );
};

export default LoginPage;
